import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  banUser,
  softDeleteUser,
  unbanUser,
  listAdminUsers,
  getAdminUserDetail,
  editAdminUserBalance,
  setAdminUserTierOverride,
  setAdminUserRefundStatus,
  revealAdminUserApiKey,
  regenerateAdminUserApiKey,
  revokeAdminUserApiKey,
  type AdminUserListResponse,
  type AdminUserDetailResponse,
  type AdminBalanceEditInput,
  type AdminBalanceEditResponse,
  type AdminTierOverrideResponse,
  type AdminRefundStatusResponse,
  type WireUserTierView,
  type AdminApiKeyRevealResponse,
  type AdminApiKeyRegenerateResponse,
  type AdminApiKeyRevokeResponse,
  type ModerationInput,
  type ModerationResponse,
  type WireAdminUserListEntry,
  type WireAdminUserDetail,
  type WireAdminUserDetailOrder,
  type WireAdminUserDetailTransaction,
  type WireAdminUserDetailApiKey,
} from '../api/endpoints.js';
import { useApiMutation, useApiQuery, useDebouncedValue, useVisiblePolling } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  IdMono,
  Input,
  LoadingState,
  PageHead,
  Select,
  StatusBadge,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';

const PAGE_SIZE = 50;

type StatusFilter = 'all' | 'active' | 'banned' | 'deleted';
type SortBy = 'createdAt' | 'updatedAt' | 'email' | 'username' | 'balance' | 'isOnlineNow';
type SortOrder = 'asc' | 'desc';

/** micro-USD → "$X.XX" */
function microToUsd(micro: number): string {
  return `$${(micro / 1_000_000).toFixed(2)}`;
}
/** basis points → "X.XX%" */
function bpsToPct(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}
/** Compact tier label for the list column. */
function tierLabel(tier: WireUserTierView): string {
  const lt = tier.lifetime.name ?? '—';
  const wk = tier.weekly.name ?? '—';
  return `${lt} / ${wk}`;
}

export function UserInspectPage(): JSX.Element {
  const { actorId: routeId } = useParams();
  const actorId = routeId ?? '';
  return actorId ? <UserDetailPanel actorId={actorId} /> : <UserListPanel />;
}

// ---------------------------------------------------------------------------
// List panel
// ---------------------------------------------------------------------------

function UserListPanel(): JSX.Element {
  const navigate = useNavigate();
  const canList = useCan('user:list');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Live search: query per typing-pause (debounced), no button click needed.
  // The form's onSubmit still flushes immediately on Enter.
  const debouncedSearch = useDebouncedValue(searchDraft, 300);
  useEffect(() => {
    setSearch(debouncedSearch.trim());
    setPage(1);
  }, [debouncedSearch]);

  const query = useApiQuery<AdminUserListResponse>(
    (options) =>
      listAdminUsers(options, {
        page,
        limit: PAGE_SIZE,
        search,
        status,
        sortBy,
        sortOrder,
      }),
    [page, search, status, sortBy, sortOrder],
  );
  useVisiblePolling(query.reload, 5000, canList);

  function onSubmitSearch(ev: FormEvent): void {
    ev.preventDefault();
    setPage(1);
    setSearch(searchDraft.trim());
  }

  // Click a sortable column header: toggle direction if already active, else
  // switch to it (default DESC). Global sort — applies to the whole list.
  function onSort(col: SortBy): void {
    if (sortBy === col) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
    setPage(1);
  }

  if (!canList) {
    return (
      <div>
        <PageHead
          crumb={<>Yönetim <Icon.ChevronRight size={11} /> Kullanıcılar</>}
          title="Kullanıcılar"
          subtitle="Listeleme için yetki gerekiyor."
        />
        <ErrorState reason="capability_missing:user:list" />
      </div>
    );
  }

  return (
    <div>
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Kullanıcılar</>}
        title="Kullanıcılar"
        subtitle={`Tüm kullanıcılar — arama, durum filtresi ve sayfa başına ${PAGE_SIZE} kayıt. Detay için satıra tıklayın.`}
      />

      <Card>
        <form onSubmit={onSubmitSearch} aria-label="kullanıcı arama">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <Input
                type="search"
                placeholder="E-posta / kullanıcı adı ara…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                aria-label="arama"
                iconLeft={<Icon.Search size={14} />}
              />
            </div>
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
              aria-label="durum filtresi"
              style={{ width: 160 }}
            >
              <option value="all">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="banned">Banlı</option>
              <option value="deleted">Silinmiş</option>
            </Select>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              aria-label="sıralama"
              style={{ width: 160 }}
            >
              <option value="createdAt">Kayıt tarihi</option>
              <option value="updatedAt">Son güncelleme</option>
              <option value="email">E-posta</option>
              <option value="username">Kullanıcı adı</option>
              <option value="balance">Bakiye</option>
              <option value="isOnlineNow">Çevrim içi</option>
            </Select>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              aria-label="sıralama yönü"
              style={{ width: 130 }}
            >
              <option value="desc">↓ Azalan</option>
              <option value="asc">↑ Artan</option>
            </Select>
            <Button type="submit" variant="primary" icon={<Icon.Search size={14} />}>
              Ara
            </Button>
          </div>
        </form>
      </Card>

      {query.kind === 'loading' ? <LoadingState /> : null}
      {query.refreshError && <Banner tone="warn">Liste güncellenemedi; aktiflik bilgisi bilinmiyor.</Banner>}
      {query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={query.reload} />
      ) : null}
      {query.kind === 'ok' ? (
        <UserListResults
          data={query.data.data}
          presenceStale={!!query.refreshError}
          page={page}
          onPageChange={setPage}
          onOpen={(actor) => navigate(`/users/${encodeURIComponent(actor)}`)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
        />
      ) : null}
    </div>
  );
}

function UserListResults({
  data,
  presenceStale,
  page,
  onPageChange,
  onOpen,
  sortBy,
  sortOrder,
  onSort,
}: {
  readonly data: AdminUserListResponse['data'];
  readonly presenceStale: boolean;
  readonly page: number;
  readonly onPageChange: (n: number) => void;
  readonly onOpen: (actorId: string) => void;
  readonly sortBy: SortBy;
  readonly sortOrder: SortOrder;
  readonly onSort: (col: SortBy) => void;
}): JSX.Element {
  if (data.users.length === 0) {
    return <EmptyState label="Bu filtrelerle eşleşen kullanıcı yok." />;
  }
  // Clickable sort header — uses the existing TH pass-through (onClick + style),
  // no shared-component change. Arrow shows the active column + direction.
  const SortTH = ({ col, children }: { readonly col: SortBy; readonly children: React.ReactNode }): JSX.Element => (
    <TH
      onClick={() => onSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      title="Sırala"
    >
      {children}
      <span style={{ opacity: sortBy === col ? 1 : 0.25, marginLeft: 4 }}>
        {sortBy === col ? (sortOrder === 'desc' ? '↓' : '↑') : '↕'}
      </span>
    </TH>
  );
  return (
    <Card flush>
      <CardHeader
        title={
          <span className="t-muted">
            Toplam: <strong style={{ color: 'var(--text-primary)' }}>{data.total.toLocaleString('tr-TR')}</strong>{' '}
            (sayfa {data.currentPage} / {data.totalPages})
          </span>
        }
      />
      <CardBody tight style={{ padding: 0 }}>
        <Table dense data-testid="admin-users-table">
          <THead>
            <TRow>
              <SortTH col="email">E-posta</SortTH>
              <SortTH col="username">Kullanıcı adı</SortTH>
              <TH>Durum</TH>
              <SortTH col="isOnlineNow">Aktiflik</SortTH>
              <TH>Seviye (Kalıcı / Haftalık)</TH>
              <TH>İndirim</TH>
              <TH>İade</TH>
              <SortTH col="balance">Bakiye</SortTH>
              <SortTH col="createdAt">Kayıt tarihi</SortTH>
            </TRow>
          </THead>
          <TBody>
            {data.users.map((u) => (
              <UserListRow key={u.actorId} u={u} onOpen={onOpen} presenceStale={presenceStale} />
            ))}
          </TBody>
        </Table>
      </CardBody>
      <div style={{ padding: 12 }}>
        <Pagination page={page} totalPages={data.totalPages} onChange={onPageChange} />
      </div>
    </Card>
  );
}

function UserListRow({
  u,
  onOpen,
  presenceStale,
}: {
  readonly u: WireAdminUserListEntry;
  readonly onOpen: (actorId: string) => void;
  readonly presenceStale: boolean;
}): JSX.Element {
  return (
    <TRow
      clickable
      onClick={() => onOpen(u.actorId)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(u.actorId);
      }}
    >
      <TD>{u.email ?? '—'}</TD>
      <TD>{u.username ?? u.displayName ?? '—'}</TD>
      <TD>{userStatusBadge(u)}</TD>
      <TD className="t-xs">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            className={!presenceStale && u.presenceAvailable !== false && u.isOnlineNow ? 'kop-presence-dot kop-presence-dot--on' : 'kop-presence-dot'}
            aria-hidden="true"
          />
          {presenceStale || u.presenceAvailable === false ? 'bilinmiyor' : u.isOnlineNow ? 'çevrimiçi' : `${u.lastActiveSource === 'last_login' ? 'Giriş: ' : ''}${presenceRelative(u.lastActiveAt)}`}
        </span>
      </TD>
      <TD className="t-xs">{tierLabel(u.tier)}</TD>
      <TD className="t-mono t-num">{bpsToPct(u.tier.effectiveDiscountBps)}</TD>
      <TD>{u.refundDisabled ? <Icon.X size={14} /> : <Icon.Check size={14} />}</TD>
      <TD className="t-mono t-num">${u.balance.toFixed(2)}</TD>
      <TD className="t-mono t-xs t-tertiary">{u.createdAt.slice(0, 10)}</TD>
    </TRow>
  );
}

function userStatusBadge(u: WireAdminUserListEntry): JSX.Element {
  if (u.isBanned) return <Badge tone="danger" dot>banlı</Badge>;
  if (u.isDeleted) return <Badge tone="danger" dot>silinmiş</Badge>;
  if (!u.isActive) return <Badge tone="warn" dot>pasif</Badge>;
  return <Badge tone="success" dot>aktif</Badge>;
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  readonly page: number;
  readonly totalPages: number;
  readonly onChange: (n: number) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} icon={<Icon.ChevronLeft size={14} />}>
        Önceki
      </Button>
      <span className="t-muted t-small">
        Sayfa {page} / {totalPages}
      </span>
      <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)} iconRight={<Icon.ChevronRight size={14} />}>
        Sonraki
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function UserDetailPanel({ actorId }: { readonly actorId: string }): JSX.Element {
  const navigate = useNavigate();
  const canViewDetail = useCan('user:view_detail');
  const canBan = useCan('user:ban');
  const canUnban = useCan('user:unban');
  const canDelete = useCan('user:delete');
  const canAdjust = useCan('user:adjust_balance');

  const query = useApiQuery<AdminUserDetailResponse>(
    (options) => getAdminUserDetail(options, actorId),
    [actorId],
  );

  const reload = query.reload;
  useVisiblePolling(reload, 5000, canViewDetail);

  if (!canViewDetail) {
    return (
      <div>
        <PageHead title="Kullanıcı Detayı" />
        <ErrorState reason="capability_missing:user:view_detail" />
      </div>
    );
  }

  return (
    <div>
      <PageHead
        crumb={
          <>
            <Link to="/users" style={{ color: 'var(--text-tertiary)' }}>Kullanıcılar</Link>
            <Icon.ChevronRight size={11} /> Detay
          </>
        }
        title="Kullanıcı Detayı"
        actions={
          <Button variant="secondary" icon={<Icon.Back size={14} />} onClick={() => navigate('/users')}>
            Listeye dön
          </Button>
        }
      />
      {query.kind === 'loading' ? <LoadingState /> : null}
      {query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={reload} />
      ) : null}
      {query.kind === 'ok' ? (
        <DetailBody
          data={query.data.data}
          presenceStale={!!query.refreshError}
          actorId={actorId}
          canBan={canBan}
          canUnban={canUnban}
          canDelete={canDelete}
          canAdjust={canAdjust}
          onChanged={reload}
        />
      ) : null}
    </div>
  );
}

function DetailBody({
  data,
  presenceStale,
  actorId,
  canBan,
  canUnban,
  canDelete,
  canAdjust,
  onChanged,
}: {
  readonly data: AdminUserDetailResponse['data'];
  readonly presenceStale: boolean;
  readonly actorId: string;
  readonly canBan: boolean;
  readonly canUnban: boolean;
  readonly canDelete: boolean;
  readonly canAdjust: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="detail-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <IdentityCard
            user={data.user}
            presenceStale={presenceStale}
            totals={{ total: data.totalOrdersCount, completed: data.completedOrdersCount }}
          />
          <BalanceCard user={data.user} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TierCard user={data.user} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
          <RefundCard user={data.user} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
          <ModerationCard
            user={data.user}
            canBan={canBan}
            canUnban={canUnban}
            canDelete={canDelete}
            actorId={actorId}
            onChanged={onChanged}
          />
          <ApiKeyCard apiKeys={data.apiKeys} canAdjust={canAdjust} actorId={actorId} onChanged={onChanged} />
        </div>
      </div>
      <div className="user-history-grid">
        <RecentOrdersCard orders={data.orders} />
        <RecentTransactionsCard transactions={data.transactions} />
      </div>
    </div>
  );
}

function IdentityCard({
  user,
  totals,
  presenceStale,
}: {
  readonly user: WireAdminUserDetail;
  readonly presenceStale: boolean;
  readonly totals: { readonly total: number; readonly completed: number };
}): JSX.Element {
  const fullName = [user.profile.firstName, user.profile.lastName].filter(Boolean).join(' ') || '—';
  return (
    <Card flush data-testid="user-identity-card">
      <CardHeader title="Kimlik" actions={statusBadgeFor(user)} />
      <CardBody>
        <div className="dl-rows">
          <Row k="Actor id" v={<IdMono value={user.actorId} />} />
          <Row k="E-posta" v={user.email ?? '—'} />
          <Row k="Kullanıcı adı" v={user.username ?? '—'} />
          <Row k="Ad / Soyad" v={fullName} />
          <Row k="Rol" v={<span className="t-mono">{user.role}</span>} />
          <Row k="E-posta doğrulanmış" v={user.emailVerified ? <Icon.Check size={14} /> : <Icon.X size={14} />} />
          <Row k="Toplam sipariş" v={<span className="t-mono t-num">{totals.total}</span>} />
          <Row k="Tamamlanan sipariş" v={<span className="t-mono t-num">{totals.completed}</span>} />
          <Row k="Kayıt" v={<span className="t-mono t-xs">{fmtDate(user.createdAt)}</span>} />
          <Row k="Son giriş" v={<span className="t-mono t-xs">{user.lastLogin ? fmtDate(user.lastLogin) : 'Kayıt yok'}</span>} />
          {/* D-231 F6 — real-activity presence. */}
          <Row
            k="Durum"
            v={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span
                  className={
                    !presenceStale && user.presenceAvailable !== false && user.isOnlineNow ? 'kop-presence-dot kop-presence-dot--on' : 'kop-presence-dot'
                  }
                  aria-hidden="true"
                />
                {presenceStale || user.presenceAvailable === false ? 'bilinmiyor' : user.isOnlineNow ? 'çevrimiçi' : 'çevrimdışı'}
              </span>
            }
          />
          <Row k={user.lastActiveSource === 'last_login' ? 'Son aktif (giriş kaydı)' : 'Son aktif'} v={<span className="t-mono t-xs">{presenceRelative(user.lastActiveAt)}{presenceStale ? ' (güncellenemedi)' : ''}</span>} />
          <Row
            k="Mesaj"
            v={
              <Link className="kop-link" to={`/messages?actor=${encodeURIComponent(user.actorId)}`}>
                Sohbeti aç →
              </Link>
            }
          />
        </div>
      </CardBody>
    </Card>
  );
}

/** Compact Turkish relative time for the presence line. */
function presenceRelative(iso: string | null): string {
  if (!iso) return 'Kayıt yok';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Kayıt yok';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return d.toLocaleString();
}

function statusBadgeFor(user: WireAdminUserDetail): JSX.Element {
  if (user.isBanned) return <Badge tone="danger" dot>banlı</Badge>;
  if (user.isDeleted) return <Badge tone="danger" dot>silinmiş</Badge>;
  if (!user.isActive) return <Badge tone="warn" dot>pasif</Badge>;
  return <Badge tone="success" dot>aktif</Badge>;
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}

function BalanceCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'add' | 'subtract' | 'set'>('add');
  const [reason, setReason] = useState('');
  const mutation = useApiMutation<AdminBalanceEditInput, AdminBalanceEditResponse>(
    (opts, input) => editAdminUserBalance(opts, actorId, input),
  );
  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!canAdjust) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt)) return;
    const res = await mutation.run({ amount: amt, type, reason: reason.trim() });
    if (res.kind === 'ok') {
      setAmount('');
      setReason('');
      onChanged();
    }
  }
  return (
    <Card flush data-testid="user-balance-card">
      <CardHeader
        title="Bakiye"
        actions={<span className="t-mono t-num" style={{ fontSize: 18, fontWeight: 600 }}>${user.balance.toFixed(2)}</span>}
      />
      <CardBody>
        <form onSubmit={onSubmit} aria-label="bakiye düzenle">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="İşlem" htmlFor="balType">
              <Select id="balType" value={type} onChange={(e) => setType(e.target.value as typeof type)} disabled={!canAdjust}>
                <option value="add">Ekle</option>
                <option value="subtract">Düş</option>
                <option value="set">Ayarla</option>
              </Select>
            </Field>
            <Field label="Miktar ($)" htmlFor="balAmt">
              <Input
                id="balAmt"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={!canAdjust}
                placeholder="örn. 10.50"
              />
            </Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <Field label="Neden" htmlFor="balReason" help="Audit'e geçer; zorunlu">
              <Input
                id="balReason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={!canAdjust}
                placeholder="örn. müşteri talebi #1234"
              />
            </Field>
          </div>
          <div style={{ marginTop: 12 }}>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.state.kind === 'running'}
              disabled={!canAdjust || !amount || !reason.trim()}
            >
              Bakiyeyi güncelle
            </Button>
          </div>
        </form>
        {mutation.state.kind === 'error' ? (
          <div style={{ marginTop: 8 }}>
            <Banner tone="danger" testId="balance-edit-error">
              <code>balance</code>: <code>{mutation.state.reason}</code>
            </Banner>
          </div>
        ) : null}
        {mutation.state.kind === 'ok' ? (
          <div style={{ marginTop: 8 }}>
            <Banner tone="success">
              Bakiye güncellendi: ${mutation.state.data.data.user.balanceBefore.toFixed(2)} → ${mutation.state.data.data.user.balanceAfter.toFixed(2)}
              {' '}(fark ${mutation.state.data.data.user.difference.toFixed(2)})
            </Banner>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** Tier index → select option label using the resolved tier name when present. */
const TIER_OVERRIDE_OPTIONS = [
  { value: '', label: 'Otomatik (eşik bazlı)' },
  { value: '1', label: 'Kademe 1 (zorla)' },
  { value: '2', label: 'Kademe 2 (zorla)' },
  { value: '3', label: 'Kademe 3 (zorla)' },
  { value: '4', label: 'Kademe 4 (zorla)' },
  { value: '5', label: 'Kademe 5 (zorla)' },
];

function TierRow({ tier, totalMicro }: { readonly tier: WireUserTierView['lifetime']; readonly totalMicro: number }): JSX.Element {
  return (
    <div className="dl-rows">
      <Row k="Kademe" v={<span>{tier.name ?? '—'}{tier.source === 'override' ? ' (override)' : ''}</span>} />
      <Row k="İndirim" v={<span className="t-mono t-num">{bpsToPct(tier.discountBps)}</span>} />
      <Row k="Toplam" v={<span className="t-mono t-num">{microToUsd(totalMicro)}</span>} />
      <Row
        k="Sonraki kademe"
        v={
          tier.nextTierName && tier.nextThresholdMicro != null ? (
            <span className="t-xs">
              {tier.nextTierName} @ {microToUsd(tier.nextThresholdMicro)}
            </span>
          ) : (
            <span className="t-xs t-tertiary">En üst kademe</span>
          )
        }
      />
    </div>
  );
}

function TierCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const tier = user.tier;
  const [ol, setOl] = useState(tier.overrideLifetimeTier == null ? '' : String(tier.overrideLifetimeTier));
  const [ow, setOw] = useState(tier.overrideWeeklyTier == null ? '' : String(tier.overrideWeeklyTier));
  const mut = useApiMutation<
    { overrideLifetimeTier: number | null; overrideWeeklyTier: number | null },
    AdminTierOverrideResponse
  >((opts, body) => setAdminUserTierOverride(opts, actorId, body));
  async function onSave(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!canAdjust) return;
    const res = await mut.run({
      overrideLifetimeTier: ol === '' ? null : Number(ol),
      overrideWeeklyTier: ow === '' ? null : Number(ow),
    });
    if (res.kind === 'ok') onChanged();
  }
  return (
    <Card flush data-testid="user-tier-card">
      <CardHeader
        title="Seviye (Kalıcı / Haftalık)"
        actions={<Badge tone={tier.effectiveDiscountBps > 0 ? 'success' : 'neutral'} dot>
          Uygulanan: {bpsToPct(tier.effectiveDiscountBps)}
        </Badge>}
      />
      <CardBody>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="t-small" style={{ fontWeight: 600, marginBottom: 6 }}>Kalıcı Seviye</div>
            <TierRow tier={tier.lifetime} totalMicro={tier.lifetimeTotalMicro} />
          </div>
          <div>
            <div className="t-small" style={{ fontWeight: 600, marginBottom: 6 }}>Haftalık Seviye</div>
            <TierRow tier={tier.weekly} totalMicro={tier.weekTotalMicro} />
          </div>
        </div>
        <div className="t-xs t-tertiary" style={{ marginTop: 8 }}>
          Uygulanan indirim = Kalıcı + Haftalık (toplanır, en fazla %50).
          {tier.effectiveScope ? ` Baskın: ${tier.effectiveScope === 'lifetime' ? 'Kalıcı' : 'Haftalık'}.` : ''}
        </div>
        <form onSubmit={onSave} aria-label="seviye override" style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Kalıcı override" htmlFor="ol">
              <Select id="ol" value={ol} onChange={(e) => setOl(e.target.value)} disabled={!canAdjust} style={{ width: 200 }}>
                {TIER_OVERRIDE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
            <Field label="Haftalık override" htmlFor="ow">
              <Select id="ow" value={ow} onChange={(e) => setOw(e.target.value)} disabled={!canAdjust} style={{ width: 200 }}>
                {TIER_OVERRIDE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
          </div>
          <div style={{ marginTop: 12 }}>
            <Button type="submit" variant="primary" size="sm" disabled={!canAdjust} loading={mut.state.kind === 'running'}>
              Override kaydet
            </Button>
          </div>
        </form>
        {mut.state.kind === 'error' ? (
          <div style={{ marginTop: 8 }}><Banner tone="danger"><code>tier</code>: <code>{mut.state.reason}</code></Banner></div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function RefundCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const refundMut = useApiMutation<boolean, AdminRefundStatusResponse>(
    (opts, disabled) => setAdminUserRefundStatus(opts, actorId, disabled),
  );
  async function onRefundToggle(): Promise<void> {
    if (!canAdjust) return;
    const res = await refundMut.run(!user.refundDisabled);
    if (res.kind === 'ok') onChanged();
  }
  return (
    <Card flush data-testid="user-refund-card">
      <CardHeader
        title="İade hakkı"
        actions={
          user.refundDisabled ? (
            <Badge tone="danger" dot>İade kapalı</Badge>
          ) : (
            <Badge tone="success" dot>İade açık</Badge>
          )
        }
      />
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div className="t-xs t-tertiary">
              {user.refundDisabled ? 'Müşteri iade talep edemez' : 'Müşteri iade talep edebilir'}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canAdjust}
            loading={refundMut.state.kind === 'running'}
            onClick={onRefundToggle}
          >
            {user.refundDisabled ? 'İadeyi aç' : 'İadeyi kapat'}
          </Button>
        </div>
        {refundMut.state.kind === 'error' ? (
          <div style={{ marginTop: 8 }}><Banner tone="danger"><code>refund</code>: <code>{refundMut.state.reason}</code></Banner></div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ModerationCard({
  user,
  canBan,
  canUnban,
  canDelete,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canBan: boolean;
  readonly canUnban: boolean;
  readonly canDelete: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const banMut = useApiMutation<ModerationInput, ModerationResponse>(banUser);
  const unbanMut = useApiMutation<ModerationInput, ModerationResponse>(unbanUser);
  const deleteMut = useApiMutation<ModerationInput, ModerationResponse>(softDeleteUser);
  async function run(action: 'ban' | 'unban' | 'delete'): Promise<void> {
    if (!reason.trim()) return;
    const fn = action === 'ban' ? banMut.run : action === 'unban' ? unbanMut.run : deleteMut.run;
    const res = await fn({ actorId, reason: reason.trim() });
    if (res.kind === 'ok') {
      setReason('');
      onChanged();
    }
  }
  return (
    <Card flush data-testid="user-moderation-card">
      <CardHeader title="Moderasyon" />
      <CardBody>
        <Field label="Neden" htmlFor="modReason" help="Audit'e geçer; tüm aksiyonlar için zorunlu">
          <Input
            id="modReason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="örn. müşteri talebi / şüpheli aktivite"
          />
        </Field>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <Button
            variant="danger"
            disabled={!canBan || user.isBanned || !reason.trim()}
            loading={banMut.state.kind === 'running'}
            onClick={() => run('ban')}
          >
            Banla
          </Button>
          <Button
            variant="secondary"
            disabled={!canUnban || !user.isBanned || !reason.trim()}
            loading={unbanMut.state.kind === 'running'}
            onClick={() => run('unban')}
          >
            Banı kaldır
          </Button>
          <Button
            variant="danger"
            disabled={!canDelete || user.isDeleted || !reason.trim()}
            loading={deleteMut.state.kind === 'running'}
            onClick={() => run('delete')}
          >
            Soft delete
          </Button>
        </div>
        {banMut.state.kind === 'error' ? <ErrBanner label="ban" reason={banMut.state.reason} /> : null}
        {unbanMut.state.kind === 'error' ? <ErrBanner label="unban" reason={unbanMut.state.reason} /> : null}
        {deleteMut.state.kind === 'error' ? <ErrBanner label="delete" reason={deleteMut.state.reason} /> : null}
      </CardBody>
    </Card>
  );
}

function ApiKeyCard({
  apiKeys,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly apiKeys: readonly WireAdminUserDetailApiKey[];
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const active = useMemo(() => apiKeys.find((k) => k.revokedAt === null) ?? null, [apiKeys]);
  const regenMut = useApiMutation<string | null, AdminApiKeyRegenerateResponse>(
    (opts, label) => regenerateAdminUserApiKey(opts, actorId, label),
  );
  const revokeMut = useApiMutation<null, AdminApiKeyRevokeResponse>(
    (opts) => revokeAdminUserApiKey(opts, actorId),
  );
  const revealMut = useApiMutation<null, AdminApiKeyRevealResponse>(
    (opts) => revealAdminUserApiKey(opts, actorId),
  );

  async function onRegen(): Promise<void> {
    if (!canAdjust) return;
    if (!window.confirm('Yeni anahtar üretilecek ve mevcut anahtar iptal olacak. Devam?')) return;
    const res = await regenMut.run(null);
    if (res.kind === 'ok') onChanged();
  }
  async function onRevoke(): Promise<void> {
    if (!canAdjust) return;
    if (!window.confirm('Aktif API anahtarı iptal edilecek. Devam?')) return;
    const res = await revokeMut.run(null);
    if (res.kind === 'ok') onChanged();
  }
  async function onReveal(): Promise<void> {
    await revealMut.run(null);
  }

  return (
    <Card flush data-testid="user-api-key-card">
      <CardHeader title="API Anahtarı" />
      <CardBody>
        <div className="dl-rows">
          <div className="dl-row">
            <div className="dl-key">Aktif anahtar</div>
            <div className="dl-val">
              {active ? (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                  <span style={{ flexShrink: 0 }}>
                    <IdMono value={active.prefix} noCopy display={`${active.prefix}…`} />
                  </span>
                  <span className="t-tertiary t-xs">id={active.id.slice(0, 8)}…</span>
                </span>
              ) : (
                <span className="t-tertiary">yok</span>
              )}
            </div>
          </div>
          {active ? (
            <div className="dl-row">
              <div className="dl-key">Son kullanım</div>
              <div className="dl-val t-mono t-xs">{fmtDate(active.lastUsedAt)}</div>
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <Button variant="secondary" size="sm" onClick={onReveal} loading={revealMut.state.kind === 'running'} icon={<Icon.Refresh size={14} />}>
            Yenile
          </Button>
          <Button variant="primary" size="sm" disabled={!canAdjust} loading={regenMut.state.kind === 'running'} onClick={onRegen} icon={<Icon.Key size={14} />}>
            Yeni anahtar üret
          </Button>
          <Button variant="danger" size="sm" disabled={!canAdjust || !active} loading={revokeMut.state.kind === 'running'} onClick={onRevoke}>
            İptal et
          </Button>
        </div>
        {regenMut.state.kind === 'ok' ? (
          <div style={{ marginTop: 12 }} data-testid="api-key-reveal">
            <Banner tone="warn" title="Yeni anahtar (yalnızca BU yanıtta görünür)">
              <code style={{ wordBreak: 'break-all' }}>{regenMut.state.data.data.apiKey}</code>
              <div className="t-xs t-tertiary" style={{ marginTop: 6 }}>
                Hemen güvenli bir yere kaydedin. Sayfa yenilenince bir daha gösterilmez.
              </div>
            </Banner>
          </div>
        ) : null}
        {regenMut.state.kind === 'error' ? <ErrBanner label="api-key-regen" reason={regenMut.state.reason} /> : null}
        {revokeMut.state.kind === 'error' ? <ErrBanner label="api-key-revoke" reason={revokeMut.state.reason} /> : null}
      </CardBody>
    </Card>
  );
}

function RecentOrdersCard({
  orders,
}: {
  readonly orders: readonly WireAdminUserDetailOrder[];
}): JSX.Element {
  return (
    <Card flush>
      <CardHeader title="Son 10 sipariş" />
      <CardBody tight style={{ padding: orders.length === 0 ? 20 : 0 }}>
        {orders.length === 0 ? (
          <EmptyState label="Bu kullanıcının siparişi yok." />
        ) : (
          <Table dense>
            <THead>
              <TRow>
                <TH>Sipariş</TH>
                <TH>Durum</TH>
                <TH>Mail</TH>
                <TH>SKU</TH>
                <TH>Sağlayıcı</TH>
                <TH>Tutar</TH>
                <TH>Açılış</TH>
              </TRow>
            </THead>
            <TBody>
              {orders.map((o) => (
                <TRow key={o.orderId}>
                  <TD>
                    <Link to={`/orders/${encodeURIComponent(o.orderId)}`}>
                      <IdMono value={o.orderId} />
                    </Link>
                  </TD>
                  <TD><StatusBadge status={o.state} /></TD>
                  {/* D-232 — inline "mail/code arrived?" per order (provider_inbox_messages). */}
                  <TD>
                    {o.codeReceived ? (
                      <Badge tone="success" dot title={o.codeReceivedAt ? fmtDate(o.codeReceivedAt) : 'mail geldi'}>geldi</Badge>
                    ) : (
                      <Badge tone="neutral" dot>bekliyor</Badge>
                    )}
                  </TD>
                  <TD className="t-mono t-xs">{o.sku ?? '—'}</TD>
                  <TD className="t-mono t-xs">{o.providerId ?? '—'}</TD>
                  <TD className="t-mono t-num">{o.finalPriceMinor !== null ? `$${(o.finalPriceMinor / 100).toFixed(2)}` : '—'}</TD>
                  <TD className="t-mono t-xs t-tertiary">{fmtDate(o.openedAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function RecentTransactionsCard({
  transactions,
}: {
  readonly transactions: readonly WireAdminUserDetailTransaction[];
}): JSX.Element {
  return (
    <Card flush>
      <CardHeader title="Son 10 işlem" />
      <CardBody tight style={{ padding: transactions.length === 0 ? 20 : 0 }}>
        {transactions.length === 0 ? (
          <EmptyState label="Bu kullanıcının işlemi yok." />
        ) : (
          <Table dense>
            <THead>
              <TRow>
                <TH>Tip</TH>
                <TH>Tutar</TH>
                <TH>Durum</TH>
                <TH>Açıklama</TH>
                <TH>Tarih</TH>
              </TRow>
            </THead>
            <TBody>
              {transactions.map((t) => (
                <TRow key={t.id}>
                  <TD className="t-mono t-xs">{t.type}</TD>
                  <TD className="t-mono t-num">${t.amount.toFixed(2)} {t.currency}</TD>
                  <TD><StatusBadge status={t.status} /></TD>
                  <TD>{t.description ?? '—'}</TD>
                  <TD className="t-mono t-xs t-tertiary">{fmtDate(t.createdAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function ErrBanner({ label, reason }: { readonly label: string; readonly reason: string }): JSX.Element {
  return (
    <div style={{ marginTop: 8 }}>
      <Banner tone="danger"><code>{label}</code>: <code>{reason}</code></Banner>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR');
  } catch {
    return iso;
  }
}
